import { Box, chakra, Checkbox, Flex, Select, Text } from "@chakra-ui/react";
import { Messages } from "../../../core/api";
import { auth } from "../../../core/auth";
import { FC_AGENCY_ID } from "../../../shared/consts";

interface POCItemTaskBodyProps {
  item: Messages["PatientDocumentPOCItem"];
  currentAnswer: Messages["POCItemsDocumentAnswer"] | undefined;
  readonly: boolean;
  onWhenToPerformChange: (
    name: string,
    whenToPerform: Messages["TaskWhenToPerformNursing"]
  ) => void;
  onFrequencyChange: (name: string, frequency: number, visitsInAWeek: number) => void;
}

export function POCItemTaskBody({
  currentAnswer,
  item,
  readonly,
  onWhenToPerformChange,
  onFrequencyChange,
}: POCItemTaskBodyProps) {
  const isFCAgency = auth.getAuthOrFail().data.agency.id === FC_AGENCY_ID;

  const handleFrequencySelect = (value: string | null, isVisitsPerWeek: boolean) => {
    if (currentAnswer?.attributes?.type !== "OnDaysOfWeek") {
      return;
    }
    const numValue = value === null || value === "" ? null : parseInt(value);
    onFrequencyChange(
      item.name,
      isVisitsPerWeek ? currentAnswer.attributes.frequency.frequency : numValue ?? 1,
      isVisitsPerWeek ? numValue ?? 7 : currentAnswer.attributes.frequency.visitsInAWeek
    );
  };

  return (
    <BodyContainer>
      <Flex gap={4}>
        {!isFCAgency && (
          <>
            <TypeContainer>
              <Checkbox
                isChecked={currentAnswer?.attributes?.type === "EveryVisit"}
                isDisabled={readonly}
                onChange={() => onWhenToPerformChange(item.name, { type: "EveryVisit" })}
              >
                Every Visit
              </Checkbox>
            </TypeContainer>

            <TypeContainer>
              <Checkbox
                isChecked={currentAnswer?.attributes?.type === "AsRequestedByPatient"}
                isDisabled={readonly}
                onChange={() => onWhenToPerformChange(item.name, { type: "AsRequestedByPatient" })}
              >
                As Needed
              </Checkbox>
            </TypeContainer>

            <TypeContainer>
              <Checkbox
                isChecked={currentAnswer?.attributes?.type === "OnDaysOfWeek"}
                isDisabled={readonly}
                onChange={() =>
                  onWhenToPerformChange(item.name, {
                    type: "OnDaysOfWeek",
                    frequency: { frequency: 1, visitsInAWeek: 7 },
                  })
                }
              >
                Days a Week
              </Checkbox>
            </TypeContainer>
          </>
        )}

        <TypeContainer>
          <Checkbox
            isChecked={currentAnswer?.attributes?.type === "Daily"}
            isDisabled={readonly}
            onChange={() => onWhenToPerformChange(item.name, { type: "Daily" })}
          >
            Daily
          </Checkbox>
        </TypeContainer>

        <TypeContainer>
          <Checkbox
            isChecked={currentAnswer?.attributes?.type === "Weekly"}
            isDisabled={readonly}
            onChange={() => onWhenToPerformChange(item.name, { type: "Weekly" })}
          >
            Weekly
          </Checkbox>
        </TypeContainer>

        <TypeContainer>
          <Checkbox
            isChecked={currentAnswer?.attributes?.type === "1-2xWeekly"}
            isDisabled={readonly}
            onChange={() => onWhenToPerformChange(item.name, { type: "1-2xWeekly" })}
          >
            1-2x Weekly
          </Checkbox>
        </TypeContainer>

        {isFCAgency && (
          <>
            <TypeContainer>
              <Checkbox
                isChecked={currentAnswer?.attributes?.type === "DailyAsNeeded"}
                isDisabled={readonly}
                onChange={() => onWhenToPerformChange(item.name, { type: "DailyAsNeeded" })}
              >
                Daily As Needed
              </Checkbox>
            </TypeContainer>

            <TypeContainer>
              <Checkbox
                isChecked={currentAnswer?.attributes?.type === "WeeklyAsNeeded"}
                isDisabled={readonly}
                onChange={() => onWhenToPerformChange(item.name, { type: "WeeklyAsNeeded" })}
              >
                Weekly As Needed
              </Checkbox>
            </TypeContainer>

            <TypeContainer>
              <Checkbox
                isChecked={currentAnswer?.attributes?.type === "1-2xWeeklyAsNeeded"}
                isDisabled={readonly}
                onChange={() => onWhenToPerformChange(item.name, { type: "1-2xWeeklyAsNeeded" })}
              >
                1-2x Weekly As Needed
              </Checkbox>
            </TypeContainer>
          </>
        )}
      </Flex>

      {currentAnswer?.attributes?.type === "OnDaysOfWeek" && (
        <Box>
          <Flex align="center" gap={2} justify="flex-start">
            <Text>Frequency:</Text>
            <Select
              isDisabled={readonly}
              value={currentAnswer.attributes.frequency.frequency ?? ""}
              w="auto"
              onChange={(e) => handleFrequencySelect(e.target.value, false)}
            >
              <option value="" />
              {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
            </Select>
            <Text>Visits Per Week</Text>
          </Flex>
        </Box>
      )}
    </BodyContainer>
  );
}

const BodyContainer = chakra(Flex, {
  baseStyle: {
    flexDirection: "column",
    gap: 2,
  },
});

const TypeContainer = chakra(Flex, {
  baseStyle: {
    margin: "4px 0",
    direction: "row",
    alignItems: "center",
    gap: "8px",
    alignContent: "center",
  },
});
